import React, { useState, useContext } from "react";
import type {
  IPaymentMode,
  ITenantNameAndId,
} from "../../../../../../types/types";
import { useFieldArray } from "react-hook-form";
import {
  useStoreState,
  promiseAllSettledLogAndThrow,
  useFormWrapper,
} from "../../../../../../util/util";
import { Form, FormItems } from "../../../../../../layout/FormLayout";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import {
  SaveButtonPrimaryMedium,
  InputRow,
  ButtonContainer,
  FieldContainer,
} from "../../shared";
import {
  DeleteButton,
  InvisibleButton,
  ButtonWithConfirmDialog,
} from "../../../../../../components/Buttons/Buttons";
import { PlusIcon } from "../../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import type { AxiosResponse } from "axios";
import Axios from "axios";
import { endpoints } from "../../../../../../endpoints";
import * as Yup from "yup";
import { strings } from "../../../../../../util/strings";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTheme } from "styled-components/macro";
import { ConfirmDialog } from "../../../../../../components/ConfirmDialog/ConfirmDialog";
import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";

type FormValues = {
  paymentModeFields: IPaymentMode[];
};

type ConfirmDeleteState = {
  message: string;
  formModes: IPaymentMode[];
  modesToDelete: IPaymentMode[];
};

// TODO: to get the "This is a required field" message to appear on the UI,
// I think we need to not use `Yup.array()` and instead dynamically create a
// schema that includes each of the rows of fields in it.
const editPaymentModesFormSchema = (t: TFunction) =>
  Yup.object()
    .shape({
      paymentModeFields: Yup.array(
        Yup.object()
          .shape({
            name: Yup.string().required(strings(t).thisIsARequiredField),
            id: Yup.string().notRequired(),
          })
          .defined()
      ).defined(),
    })
    .defined();

export const EditPaymentModes = ({
  paymentModes,
  mutatePaymentModes,
  closeEditPaymentModes,
}: {
  paymentModes: IPaymentMode[];
  mutatePaymentModes: () => void;
  closeEditPaymentModes: () => void;
}) => {
  const theme = useTheme();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storefront_id } = useStoreState();
  const { t } = useTranslation();

  const [submitting, setSubmitting] = useState(false);

  // When confirmDeleteState is not null, a dialog is shown.
  const [confirmDeleteState, setConfirmDeleteState] =
    useState<ConfirmDeleteState | null>(null);

  const { register, handleSubmit, formState, errors, control } = useFormWrapper(
    {
      defaultValues: {
        paymentModeFields:
          paymentModes.length === 0 ? [{ name: "", id: "" }] : paymentModes,
      },
      resolver: yupResolver(editPaymentModesFormSchema(t)),
    }
  );

  const { fields, append, remove } = useFieldArray<IPaymentMode, "key">({
    control,
    name: "paymentModeFields",
    keyName: "key",
  });

  const onSubmit = async (formValues: FormValues) => {
    setSubmitting(true);
    const formModes = formValues.paymentModeFields;
    try {
      const modesToDelete = paymentModes.filter((mode) => {
        const formHasMode = formModes.find((fm) => fm.id === mode.id);
        return !formHasMode;
      });

      const defaultModeCheckPromises = modesToDelete.map((mode) => {
        return Axios.get<ITenantNameAndId[]>(
          endpoints.v1_storefronts_id_tenant_defaults_paymentModes_id(
            storefront_id,
            mode.id
          )
        );
      });
      const modeCheckResults = await promiseAllSettledLogAndThrow(
        defaultModeCheckPromises
      );

      const buyerNames = modeCheckResults.reduce((names: string[], result) => {
        if ("value" in result) {
          // For TypeScript, should always be the case.
          const moreNames = result.value.data.map((record) => record.name);
          return [...names, ...moreNames];
        }
        return names;
      }, []);

      if (buyerNames.length > 0) {
        const buyerNamesString = buyerNames.join(", ");

        const confirmationMessage =
          `You are about to delete payment methods that are set as the default ` +
          `payment method for these customers: ${buyerNamesString}. If you ` +
          `proceed then their default payment method will be cleared, ` +
          `and you should re-set it.`;

        setConfirmDeleteState({
          message: confirmationMessage,
          formModes,
          modesToDelete,
        });
      } else {
        continueSubmitting(formModes, modesToDelete);
      }
    } catch (error) {
      notifyError("An error occurred, please try again", { error });
      setSubmitting(false);
    }
  };

  const continueSubmitting = async (
    formModes: IPaymentMode[],
    modesToDelete: IPaymentMode[]
  ) => {
    try {
      const deletePromises = modesToDelete.map((mode) => {
        // Mode was deleted from the form -> delete.
        return Axios.delete(
          endpoints.v1_storefronts_id_paymentModes_id(storefront_id, mode.id)
        );
      });

      const patchPromises = formModes.reduce(
        (promises: Promise<AxiosResponse<any>>[], formMode) => {
          if (formMode.id) {
            const oldMode = paymentModes.find((md) => md.id === formMode.id);

            if (oldMode && oldMode.name !== formMode.name) {
              // The mode already exists and the name changed -> patch.
              return [
                ...promises,
                Axios.patch(
                  endpoints.v1_storefronts_id_paymentModes_id(
                    storefront_id,
                    formMode.id
                  ),
                  { name: formMode.name }
                ),
              ];
            }
          }
          return promises;
        },
        []
      );

      // DELETE requests have to happen before POST requests, to handle the case
      // where the mode was deleted and then re-introduced (new mode with same
      // name) in the same editing pass. This is because on the backend if the
      // user creates a new mode with the same name as a deleted mode,
      // the deleted mode is re-used (un-deleted, and description updated) that
      // way there is consistency in the IDs of the modes if they are deleted
      // and re-created.
      await promiseAllSettledLogAndThrow(deletePromises);

      const postPromises = formModes.reduce(
        (promises: Promise<AxiosResponse<any>>[], formMode) => {
          if (!formMode.id) {
            // New mode -> post.
            return [
              ...promises,
              Axios.post(
                endpoints.v1_storefronts_id_paymentModes(storefront_id),
                { name: formMode.name }
              ),
            ];
          }
          return promises;
        },
        []
      );
      await promiseAllSettledLogAndThrow(patchPromises);
      await promiseAllSettledLogAndThrow(postPromises);

      notifySuccess("Changes saved");
      mutatePaymentModes();
      closeEditPaymentModes();
      setSubmitting(false);
    } catch (error) {
      notifyError("There was an error saving the changes", { error });
      setSubmitting(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormItems>
          {fields.map((field, index) => {
            return (
              <InputRow key={field.key || "formRow" + index}>
                <FieldContainer>
                  <TextField
                    label={`Payment Method ${index + 1}`}
                    // The name property needs to be in this format.
                    name={`paymentModeFields[${index}].name`}
                    defaultValue={field.name}
                    theref={register({ required: true })}
                    errors={errors}
                    formState={formState}
                    type={"text"}
                  />
                  <input
                    name={`paymentModeFields[${index}].id`}
                    defaultValue={field.id}
                    ref={register({ required: false })}
                    type={"hidden"}
                  />
                </FieldContainer>
                <ButtonContainer>
                  <ButtonWithConfirmDialog
                    Button={DeleteButton}
                    confirmMessage={
                      "Are you sure you want to remove this item?"
                    }
                    handleConfirm={() => remove(index)}
                    // Adding `type="button"` prevents the default (form
                    // submission) so we don't need to use event.preventDefault().
                    type="button"
                    disabled={fields.length === 1}
                    testid={`delete-payment-mode-field-${index}`}
                  />
                </ButtonContainer>
                {index === fields.length - 1 && (
                  <ButtonContainer>
                    <InvisibleButton
                      // Adding `type="button"` prevents the default (form
                      // submission) so we don't need to use event.preventDefault().
                      type="button"
                      onClick={() => {
                        append({ name: "", id: "" });
                      }}
                    >
                      <PlusIcon
                        width={16}
                        height={16}
                        fill={theme.primaryIconColor}
                      />
                    </InvisibleButton>
                  </ButtonContainer>
                )}
              </InputRow>
            );
          })}
        </FormItems>
        <SaveButtonPrimaryMedium loading={submitting}>
          Save your changes
        </SaveButtonPrimaryMedium>
      </Form>
      {confirmDeleteState && (
        <ConfirmDialog
          show={!!confirmDeleteState}
          closeDialog={() => {
            setSubmitting(false);
            setConfirmDeleteState(null);
          }}
          confirmMessage={confirmDeleteState?.message || ""}
          handleConfirm={() => {
            continueSubmitting(
              confirmDeleteState.formModes,
              confirmDeleteState.modesToDelete
            );
            setConfirmDeleteState(null);
          }}
        />
      )}
    </>
  );
};
